import { getErrorMessage } from '@features/shared/api/client'
import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAppNavigation } from '@features/navigation/hooks/useAppNavigation'
import { useOptionalRouteCatalog } from '@features/navigation/components/RouteCatalogContext'
import { routeSegmentLabel } from '@features/navigation/logic/appRouteCatalog'
import type { RouteNode } from '@features/navigation/logic/routeCatalog'
import {
  findRouteParamSource,
  type RouteParamFreshness,
  type RouteParamOption,
} from '@features/navigation/logic/routeParamSources'
import { useUpdateOwnPreference } from '@features/settings/hooks/useUpdateOwnPreference'
import { useSettingsStore, type SidebarTableSort } from '@features/settings/stores/useSettingsStore'
import { useToast } from '@features/shared/components/Toast/ToastContext'
import { useStableCallback } from '@features/shared/hooks/useStableCallback'
import type {
  SidebarFavorite,
  SidebarFavoritesResponse,
  SidebarHiddenResponse,
} from '../api/contracts'
import {
  addFavorite,
  getFavorites,
  recordFavoritesOpened,
  removeFavorite,
  reorderFavorites,
} from '../api/favorites.api'
import { sidebarQueryKeys } from '../api/queryKeys'
import {
  describeFavorite,
  isPathWithin,
  normalizeFavoritePath,
  type FavoriteDescription,
} from '../logic/favorites'
import { useFavoriteDescriptionsStore } from '../stores/useFavoriteDescriptionsStore'

const FAVORITES_STALE_MS = 60_000
// Metadata often lands in bursts (a page's queries); names are worked out once after.
const DESCRIBE_DEBOUNCE_MS = 150

/**
 * The user's favorites, fetched with the navigation (ConfigurationController),
 * so their names are worked out before the Favorites menu is ever opened.
 */
export const favoritesQuery = {
  queryKey: sidebarQueryKeys.favorites,
  queryFn: getFavorites,
  staleTime: FAVORITES_STALE_MS,
} as const

/** The user's favorites, in their own order. */
export function useFavoritesQuery() {
  return useQuery(favoritesQuery)
}

/** The page at `path` is one of the user's favorites. */
export function useIsFavorite(path: string | null): boolean {
  const { data } = useFavoritesQuery()
  if (!path || !data) return false
  const normalized = normalizeFavoritePath(path)
  return data.favorites.some((favorite) => favorite.path === normalized)
}

type FavoriteChange =
  | { kind: 'add'; path: string }
  | { kind: 'remove'; path: string }
  | { kind: 'reorder'; paths: string[] }
  /** Favorites just opened, stamped for Recency. */
  | { kind: 'opened'; paths: string[] }

let latestChangeId = 0

// Changes go to the server one at a time, in the order they were made, so a
// stamp for a page just hearted lands after the heart, and hiding a page just
// hearted lands after the heart too (useHiddenPages shares the turn). (A
// shared mutation scope can leave a change queued during another's request
// paused for good.)
let pendingChanges: Promise<unknown> = Promise.resolve()
export function inTurn<T>(send: () => Promise<T>): Promise<T> {
  const turn = pendingChanges.then(send, send)
  pendingChanges = turn.catch(() => undefined)
  return turn
}

function applyChange(
  favorites: readonly SidebarFavorite[],
  change: FavoriteChange
): SidebarFavorite[] {
  if (change.kind === 'add') {
    if (favorites.some((favorite) => favorite.path === change.path)) return [...favorites]
    return [
      ...favorites,
      { path: change.path, added_at: new Date().toISOString(), last_opened_at: null },
    ]
  }
  if (change.kind === 'remove') return favorites.filter((favorite) => favorite.path !== change.path)
  if (change.kind === 'opened') {
    const now = new Date().toISOString()
    return favorites.map((favorite) =>
      change.paths.includes(favorite.path) ? { ...favorite, last_opened_at: now } : favorite
    )
  }
  const byPath = new Map(favorites.map((favorite) => [favorite.path, favorite]))
  const ordered = change.paths.flatMap((path) => byPath.get(path) ?? [])
  return [...ordered, ...favorites.filter((favorite) => !change.paths.includes(favorite.path))]
}

/**
 * Adds, removes, reorders and stamps favorites. Each change shows at once and
 * goes to the server in turn, so a stamp for a page just hearted lands after
 * the heart; a change that fails puts the list back and says so (a failed
 * stamp keeps the local one quietly: Recency is a convenience).
 */
export function useFavoriteChanges() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const mutation = useMutation<
    SidebarFavoritesResponse,
    Error,
    FavoriteChange,
    {
      previous: SidebarFavoritesResponse | undefined
      changeId: number
      /** The page was hidden: hearting it shows it again (never both). */
      unhid: boolean
    }
  >({
    mutationFn: (change) =>
      inTurn(() =>
        change.kind === 'add'
          ? addFavorite(change.path)
          : change.kind === 'remove'
            ? removeFavorite(change.path)
            : change.kind === 'reorder'
              ? reorderFavorites(change.paths)
              : recordFavoritesOpened(change.paths)
      ),
    onMutate: async (change) => {
      const changeId = ++latestChangeId
      await queryClient.cancelQueries({ queryKey: sidebarQueryKeys.favorites })
      const previous = queryClient.getQueryData<SidebarFavoritesResponse>(
        sidebarQueryKeys.favorites
      )
      queryClient.setQueryData<SidebarFavoritesResponse>(sidebarQueryKeys.favorites, {
        favorites: applyChange(previous?.favorites ?? [], change),
        updated_at: previous?.updated_at ?? null,
      })
      let unhid = false
      if (change.kind === 'add') {
        const hidden = queryClient.getQueryData<SidebarHiddenResponse>(sidebarQueryKeys.hidden)
        if (hidden?.hidden.some((page) => page.path === change.path)) {
          unhid = true
          queryClient.setQueryData<SidebarHiddenResponse>(sidebarQueryKeys.hidden, {
            ...hidden,
            hidden: hidden.hidden.filter((page) => page.path !== change.path),
          })
        }
      }
      return { previous, changeId, unhid }
    },
    onError: (error, change, context) => {
      if (change.kind === 'opened') return
      if (context?.changeId === latestChangeId && context.previous !== undefined) {
        queryClient.setQueryData(sidebarQueryKeys.favorites, context.previous)
      } else {
        void queryClient.invalidateQueries({ queryKey: sidebarQueryKeys.favorites })
      }
      addToast({
        type: 'error',
        title: "Couldn't update favorites",
        message: getErrorMessage(error),
      })
    },
    onSuccess: (response, _change, context) => {
      // A later change is already showing; its own answer brings the list.
      if (context?.changeId === latestChangeId) {
        queryClient.setQueryData(sidebarQueryKeys.favorites, response)
      }
    },
    onSettled: (_response, _error, _change, context) => {
      if (context?.unhid) void queryClient.invalidateQueries({ queryKey: sidebarQueryKeys.hidden })
    },
  })

  const { mutate } = mutation
  return useMemo(
    () => ({
      add: (path: string) => mutate({ kind: 'add', path: normalizeFavoritePath(path) }),
      remove: (path: string) => mutate({ kind: 'remove', path: normalizeFavoritePath(path) }),
      reorder: (paths: string[]) => mutate({ kind: 'reorder', paths }),
      opened: (paths: string[]) => mutate({ kind: 'opened', paths }),
    }),
    [mutate]
  )
}

/**
 * Stamps the favorites the user opens, from the page itself or any page under
 * it (a page hearted while open counts), so the Favorites menu's Recency sort
 * can put them first.
 */
export function useRecordFavoriteOpens() {
  const { pathname } = useLocation()
  const { data } = useFavoritesQuery()
  const { opened: recordOpened } = useFavoriteChanges()
  const recordedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!data) return
    const opened = data.favorites
      .filter((favorite) => isPathWithin(pathname, favorite.path))
      .map((favorite) => favorite.path)
    const key = `${pathname}|${opened.join('|')}`
    if (recordedRef.current === key) return
    recordedRef.current = key
    if (opened.length > 0) recordOpened(opened)
  }, [data, pathname, recordOpened])
}

/** A list a description was read from, and how to hear when it changes. */
export interface DescribedFrom {
  /** The source and its parameters: the same for every read of that list. */
  key: string
  /** Calls `onChange` when the list changes, until the returned function is called. */
  watch: (onChange: () => void) => () => void
}

/**
 * Names addresses from their pages' metadata (describeFavorite), sharing one
 * lookup per list among them: addresses often share a table's. `onRead` hears
 * of each list a name was read from, once it's been read.
 */
export function createRouteDescriber(
  root: RouteNode,
  /** The areas the user may open (`/tables`); null when that's unknown, so none is out of reach. */
  availableAreas: ReadonlySet<string> | null,
  queryClient: QueryClient,
  onRead?: (list: DescribedFrom) => void
) {
  const lookups = new Map<string, Promise<RouteParamOption[] | null>>()
  return (path: string, freshness: RouteParamFreshness) =>
    describeFavorite(root, path, {
      isAreaAvailable: (segment) => availableAreas?.has(`/${segment}`) ?? true,
      segmentLabel: routeSegmentLabel,
      loadParamOptions: (paramNode, params) => {
        const listKey = `${paramNode.pattern}|${JSON.stringify(params)}`
        const key = `${freshness}|${listKey}`
        let lookup = lookups.get(key)
        if (!lookup) {
          const source = findRouteParamSource(paramNode.pattern)
          lookup = source?.loadOptions(params, queryClient, freshness) ?? Promise.resolve(null)
          lookups.set(key, lookup)
          if (source && onRead) {
            void lookup.then(() =>
              onRead({
                key: listKey,
                watch: (onChange) => source.watchOptions(params, queryClient, onChange),
              })
            )
          }
        }
        return lookup
      },
    })
}

/**
 * Works out each favorite's name and standing from its page's metadata in the
 * background, from the moment the app loads, so the Favorites menu never waits
 * on them: from the metadata the app already has (refreshed behind it), and
 * again when the favorites, the areas the user may open, or a list a name was
 * read from change (a rename). Nothing else the app loads (records, say) names
 * them again, and the sidebar that mounts this doesn't render for it. Names
 * land together when they're at hand and one by one when they have to be
 * fetched; they're kept between menus and visits
 * (useFavoriteDescriptionsStore). A favorite whose page looks gone is checked
 * against fresh metadata, then hidden, never taken off: the server answers a
 * deleted page and one out of the user's reach the same way, so it comes back
 * if their access does. One that can't be checked (its list didn't load)
 * stays. Mounted once, by the sidebar.
 */
export function useDescribeFavorites() {
  const root = useOptionalRouteCatalog()
  const { modules, modulesPending, modulesError } = useAppNavigation()
  const queryClient = useQueryClient()
  const userId = useSettingsStore((state) => state.appUserId)
  const { data } = useFavoritesQuery()
  // Opening a favorite stamps it, a new list with the same pages: no new names.
  const pathsKey = data?.favorites.map((favorite) => favorite.path).join('\n')
  const availableAreasKey = modules.map((module) => module.pathPrefix).join('|')

  useEffect(() => {
    // Until the areas arrive, every favorite would look switched off; when
    // they can't be read, the names kept from before stay until they can.
    if (!root || pathsKey === undefined || modulesPending || modulesError || userId === null) {
      return
    }
    let cancelled = false
    const paths = pathsKey ? pathsKey.split('\n') : []
    const availableAreas = new Set(availableAreasKey.split('|'))
    useFavoriteDescriptionsStore.getState().keepOnly(userId, paths)

    // The lists the names were read from: a change to one names them again,
    // a burst of changes once.
    const watching = new Map<string, () => void>()
    let describeTimer: ReturnType<typeof setTimeout> | null = null
    const describeAgain = () => {
      if (describeTimer !== null) clearTimeout(describeTimer)
      describeTimer = setTimeout(() => {
        describeTimer = null
        describeAll()
      }, DESCRIBE_DEBOUNCE_MS)
    }
    const watch = (list: DescribedFrom) => {
      if (cancelled || watching.has(list.key)) return
      watching.set(list.key, list.watch(describeAgain))
    }

    let described: FavoriteDescription[] = []
    let flushTimer: ReturnType<typeof setTimeout> | null = null
    const keep = (description: FavoriteDescription) => {
      described.push(description)
      flushTimer ??= setTimeout(() => {
        flushTimer = null
        useFavoriteDescriptionsStore.getState().put(userId, described)
        described = []
      }, 0)
    }

    // Names from a round a newer one has replaced are let go.
    let round = 0
    const describeAll = () => {
      const current = ++round
      const describe = createRouteDescriber(root, availableAreas, queryClient, watch)
      for (const path of paths) {
        void describe(path, 'cached').then(async (cachedDescription) => {
          // What the app had may be behind a page just made: checked before letting go.
          const description =
            cachedDescription.status === 'gone' ? await describe(path, 'fresh') : cachedDescription
          if (cancelled || current !== round) return
          keep(description)
        })
      }
    }
    describeAll()

    return () => {
      cancelled = true
      if (flushTimer !== null) clearTimeout(flushTimer)
      if (describeTimer !== null) clearTimeout(describeTimer)
      for (const unwatch of watching.values()) unwatch()
    }
  }, [availableAreasKey, modulesError, modulesPending, pathsKey, queryClient, root, userId])
}

const NO_DESCRIPTIONS: ReadonlyMap<string, FavoriteDescription> = new Map()

/** Each favorite's name and standing, as last worked out (useDescribeFavorites). */
export function useFavoriteDescriptions(): ReadonlyMap<string, FavoriteDescription> {
  const userId = useSettingsStore((state) => state.appUserId)
  const describedFor = useFavoriteDescriptionsStore((state) => state.userId)
  const descriptions = useFavoriteDescriptionsStore((state) => state.descriptions)
  return useMemo(
    () =>
      userId !== null && describedFor === userId
        ? new Map(Object.entries(descriptions))
        : NO_DESCRIPTIONS,
    [describedFor, descriptions, userId]
  )
}

/** The Favorites menu's sort, the user's own Preference. */
export function useFavoritesSort() {
  const sort = useSettingsStore((s) => s.sidebarFavoritesSort)
  const updateOwnPreference = useUpdateOwnPreference()
  const setSort = useStableCallback((next: SidebarTableSort) => {
    if (next === useSettingsStore.getState().sidebarFavoritesSort) return
    void updateOwnPreference('sidebar_favorites_sort', 'sidebarFavoritesSort', next)
  })
  return { sort, setSort }
}

/** Makes a page the user's Home Page, the page Home and the logo open. */
/** The page at `path` is the one Home and the logo open. */
export function useIsHomePage(path: string | null): boolean {
  const homePage = useSettingsStore((state) => state.homePage)
  return path !== null && homePage === normalizeFavoritePath(path)
}

export function useSetHomePage() {
  const updateOwnPreference = useUpdateOwnPreference()
  const { addToast } = useToast()
  return useStableCallback((path: string, name: string) => {
    void updateOwnPreference('home_page', 'homePage', normalizeFavoritePath(path))
    addToast({ type: 'success', title: 'Home Page set', message: `Home now opens ${name}.` })
  })
}
